import { useRef } from 'react';
import { Terminal, Activity } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// 👇 Описываем форму лога
interface LogEntry {
  id: number;
  time: string;
  message: string;
  type: 'INFO' | 'WARN' | 'ERROR';
}

interface SystemLogsProps {
  logs: LogEntry[]; // Принимаем массив (где [0] - это самый новый лог)
}

export default function SystemLogs({ logs }: SystemLogsProps) {
  const { t } = useTranslation();
  // Ref нам больше не нужен для скролла, но оставим для контейнера
  const listRef = useRef<HTMLDivElement>(null);

  // ❌ МЫ УБРАЛИ useEffect С AUTO-SCROLL
  // Теперь новые логи будут просто появляться сверху, и скролл не будет прыгать вниз.

  const renderLogItem = (log: LogEntry) => {
    let colorClass = 'text-slate-300';
    
    if (log.type === 'ERROR') {
        colorClass = 'text-red-400 font-bold';
    } else if (log.type === 'WARN') {
        colorClass = 'text-yellow-400 font-medium';
    } else if (log.message.includes('ENTRY') || log.message.includes('ВХОД')) {
        colorClass = 'text-emerald-400 font-bold';
    } else if (log.message.includes('EXIT') || log.message.includes('ВЫХОД')) {
        colorClass = 'text-blue-400 font-bold';
    }

    return (
      <div key={log.id} className="flex gap-3 border-b border-white/5 pb-1.5 mb-1 last:border-0 hover:bg-white/5 transition-colors px-2 rounded py-1 animate-in fade-in slide-in-from-top-2 duration-300">
        <span className="text-slate-500 font-mono text-[10px] pt-0.5 shrink-0 opacity-70">
          [{log.time}]
        </span>
        <span className={`${colorClass} break-all leading-tight text-xs font-mono`}>
          {log.message}
        </span>
      </div>
    );
  };

  return (
    <div className="bg-[#1e293b] border border-slate-800 rounded-[24px] p-5 h-[350px] flex flex-col shadow-xl relative overflow-hidden">
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
      `}</style>

      <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <h3 className="text-slate-400 font-bold flex items-center gap-2 text-xs uppercase tracking-widest">
            <Terminal size={14} className="text-blue-500" /> {t('platform.logs.title')}
          </h3>
          <span className="text-[10px] text-slate-600 font-mono bg-slate-900 px-2 py-0.5 rounded border border-white/5">
              LIVE / {logs.length}
          </span>
      </div>
      
      <div 
        ref={listRef}
        className="flex-1 overflow-y-auto space-y-1 pr-2 custom-scrollbar"
      >
        {logs.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-600 gap-3 opacity-40">
              <Activity size={32} />
              <p className="text-xs uppercase tracking-wider">{t('platform.logs.waiting')}</p>
          </div>
        ) : (
          // Просто рендерим список. Так как в TradingService используется unshift,
          // logs[0] - это самый свежий лог, и он будет первым в списке (сверху).
          logs.map((log) => renderLogItem(log))
        )}
      </div>
    </div>
  );
}